import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPKR } from './currencyFormatter';
import { addPdfBrandLogo } from './pdfLogo';

function pick(obj, snake, camel, fallback = null) {
  if (!obj || typeof obj !== 'object') return fallback;
  if (obj[snake] != null) return obj[snake];
  if (obj[camel] != null) return obj[camel];
  return fallback;
}

function readMeta(meta, snake, camel, fallback = null) {
  if (!meta || typeof meta !== 'object') return fallback;
  if (meta[snake] != null) return meta[snake];
  if (meta[camel] != null) return meta[camel];
  return fallback;
}

export async function downloadPurchaseReceivingReceiptPdf({ businessName, branchName, purchase }) {
  const p = purchase || {};
  const items = Array.isArray(p.items) ? p.items : [];
  const meta = p.meta && typeof p.meta === 'object' ? p.meta : {};
  const receiptNo = readMeta(meta, 'receiving_receipt_no', 'receivingReceiptNo', '—');
  const receiptDate = readMeta(meta, 'receiving_receipt_date', 'receivingReceiptDate', pick(p, 'purchase_date', 'purchaseDate', '—'));
  const receivedBy = readMeta(meta, 'receiving_receipt_by', 'receivingReceiptBy', '—');

  const purchaseNo = pick(p, 'purchase_number', 'purchaseNumber', `PUR-${pick(p, 'id', 'id', '')}`);
  const vendor = p.party?.name || '—';
  const branch = branchName || p.branch?.name || '—';
  const total = parseFloat(pick(p, 'total_amount', 'totalAmount', 0)) || 0;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const margin = 14;
  const { nextY } = await addPdfBrandLogo(doc, { maxWidthMm: 30, startY: 8, gapAfterMm: 3 });
  let y = nextY;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text('Purchase Receiving Receipt', margin, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(String(businessName || 'Business'), margin, y);
  y += 5;
  doc.text(`Receipt #: ${receiptNo}`, margin, y);
  y += 5;
  doc.text(`Purchase #: ${purchaseNo}`, margin, y);
  y += 5;
  doc.text(`Vendor: ${vendor}`, margin, y);
  y += 5;
  doc.text(`Branch: ${branch}`, margin, y);
  y += 5;
  doc.text(`Receipt date: ${receiptDate || '—'}`, margin, y);
  y += 5;
  doc.text(`Received by: ${receivedBy || '—'}`, margin, y);
  y += 6;

  const body = items.map((it, idx) => {
    const qty = parseFloat(it.quantity) || 0;
    const unitPrice = parseFloat(pick(it, 'unit_price', 'unitPrice', 0)) || 0;
    const lineTotal = parseFloat(pick(it, 'total_price', 'totalPrice', qty * unitPrice)) || qty * unitPrice;
    const productName = it.product?.name || '—';
    const productCode = pick(it.product, 'product_code', 'productCode', '—');
    const variant = [it.variant?.color, it.variant?.size].filter(Boolean).join(' / ') || '—';
    return [
      String(idx + 1),
      String(productCode),
      String(productName),
      String(variant),
      String(qty),
      formatPKR(unitPrice),
      formatPKR(lineTotal)
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [['#', 'Code', 'Product', 'Variant', 'Qty', 'Unit price', 'Line total']],
    body,
    theme: 'grid',
    headStyles: { fillColor: [55, 65, 81], textColor: [255, 255, 255], fontSize: 9 },
    styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: {
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' }
    },
    margin: { left: margin, right: margin }
  });

  const finalY = doc.lastAutoTable?.finalY || (y + 40);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(`Total received value: ${formatPKR(total)}`, margin, finalY + 8);

  const safePurchaseNo = String(purchaseNo || 'purchase').replace(/[^\w-]/g, '-').slice(0, 40);
  doc.save(`receiving-receipt-${safePurchaseNo}.pdf`);
}
